import React, { useState } from 'react';
import { Button, Modal, Spinner } from "@tag/tag-components-react-v4";
import { ACCTermination } from "../types/ACCTermination";
import LockSiteService from "../services/LockSiteService";
import { ProjectAccents } from "../../General/config/projectAccents";
import PipelineStatusDisplay from "../../General/components/PipelineStatusDisplay";

interface UnlockSiteResult {
    buildUrl?: string;
    buildId?: number;
    pipelineId?: number;
}

function UnlockSiteModal(props: Readonly<{
    visible: boolean,
    selectedTermination: ACCTermination,
    handleClose: () => void,
    setAlert: Function
}>) {

    const [loading, setLoading] = useState(false);
    const [pipelineResult, setPipelineResult] = useState<UnlockSiteResult>();

    const handleConfirmUnlock = async () => {
        setLoading(true);

        try {
            const result = await LockSiteService.unlockSite(
                props.selectedTermination.appGroupName,
                Number(props.selectedTermination.clientID)
            );

            if (result.success) {
                setPipelineResult({
                    buildUrl: result.buildUrl,
                    buildId: result.buildId,
                    pipelineId: result.pipelineId
                });
            } else {
                props.setAlert({ type: 'error', message: result.errorMessage || 'Failed to trigger unlock site pipeline' });
                props.handleClose();
            }
        } catch (e: any) {
            props.setAlert({ type: 'error', message: e.message || 'An error occurred, please contact HSC DevOps' });
            props.handleClose();
        } finally {
            setLoading(false);
        }
    };

    const renderModalContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1em', padding: '1em 0' }}>
                    <Spinner />
                    <span>Triggering unlock site pipeline...</span>
                </div>
            );
        }

        if (pipelineResult) {
            return (
                <>
                    <p>Pipeline triggered successfully for <strong>{props.selectedTermination.clientName}</strong>!</p>
                    {pipelineResult.pipelineId && pipelineResult.buildId ? (
                        <PipelineStatusDisplay
                            apiBaseUrl={process.env.REACT_APP_SELF_SERVICE_CARECOMPLIANCE_API_URL}
                            pipelineId={pipelineResult.pipelineId}
                            runId={pipelineResult.buildId}
                            buildUrl={pipelineResult.buildUrl}
                        />
                    ) : pipelineResult.buildUrl ? (
                        <p>
                            <a href={pipelineResult.buildUrl} target="_blank" rel="noopener noreferrer">
                                View Pipeline Run
                            </a>
                        </p>
                    ) : pipelineResult.buildId ? (
                        <p>Pipeline Run ID: <strong>{pipelineResult.buildId}</strong></p>
                    ) : null}
                </>
            );
        }

        return (
            <>
                <p>Are you sure you want to unlock site <strong>{props.selectedTermination.clientName}</strong> ({props.selectedTermination.clientID} - {props.selectedTermination.appGroupName})?</p>
                <div style={{ display: 'flex', gap: '1em', marginTop: '1em' }}>
                    <Button text={'Confirm'} onClick={handleConfirmUnlock} />
                </div>
            </>
        );
    };

    return (
        <Modal
            visible={props.visible}
            accent={ProjectAccents.CareCompliance}
            heading={pipelineResult ? 'Unlock Site Triggered' : 'Unlock Site'}
            onCancelButtonClick={() => props.handleClose()}
            onClose={() => props.handleClose()}
            primaryButtonProps={{ buttonStyle: { 'display': 'none' } }}
        >
            <div>
                {renderModalContent()}
            </div>
        </Modal>
    );
}

export default UnlockSiteModal;
